<template>
  <div
    class="fdai"
    :class="{ 'fdai--open': isOpen }"
  >
    <div
      class="fdai-toggle"
      @click="toggle"
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="3"
          y="6"
          width="18"
          height="14"
          rx="3"
          fill="currentColor"
        />
        <circle
          cx="12"
          cy="2"
          r="1.5"
          fill="currentColor"
        />
        <line
          x1="12"
          y1="3.5"
          x2="12"
          y2="6"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <rect
          x="6.5"
          y="10"
          width="3"
          height="3"
          rx="1"
          fill="#fff"
        />
        <rect
          x="14.5"
          y="10"
          width="3"
          height="3"
          rx="1"
          fill="#fff"
        />
      </svg>
      <span>AI помощник</span>
      <span
        v-if="wsStatus === 'online'"
        class="fdai-status fdai-status--on"
      />
      <span
        v-else
        class="fdai-status fdai-status--off"
      />
      <feather-icon
        :icon="isOpen ? 'ChevronDownIcon' : 'ChevronUpIcon'"
        size="12"
      />
    </div>

    <div
      v-if="isOpen"
      class="fdai-panel"
    >
      <div
        ref="messages"
        class="fdai-messages"
      >
        <div class="fdai-msg fdai-msg--bot">
          Опишите что изменить в форме. Я передам текущий form_param боту и применю ответ.
          <div class="fdai-examples">
            <span @click="sendText('Донастрой второй уровень группировки — добавь колонки и суммы')">Донастроить уровень</span>
            <span @click="sendText('Добавь уровень группировки по полю Наименование ТРУ (text)')">+ group по полю</span>
            <span @click="sendText('Добавь уровень группировки по справочнику Подразделение (ref)')">+ group по справочнику</span>
            <span @click="sendText('Добавь фильтр по подразделению')">+ фильтр</span>
          </div>
        </div>
        <div
          v-for="(msg, mi) in messages"
          :key="mi"
          :class="'fdai-msg fdai-msg--' + msg.role"
        >
          <div
            class="fdai-msg-text"
            v-html="formatText(msg.text)"
          />
          <div
            v-if="msg.formParam && !msg.applied && !msg.discarded"
            class="fdai-msg-actions"
          >
            <button
              class="fdai-btn fdai-btn--apply"
              @click="applyMsg(mi)"
            >
              <feather-icon
                icon="CheckIcon"
                size="12"
              /> Применить
            </button>
            <button
              class="fdai-btn"
              @click="discardMsg(mi)"
            >
              Отклонить
            </button>
          </div>
          <div
            v-if="msg.applied"
            class="fdai-msg-status fdai-msg-status--ok"
          >
            ✓ Применено
          </div>
          <div
            v-if="msg.discarded"
            class="fdai-msg-status fdai-msg-status--no"
          >
            ✗ Отклонено
          </div>
        </div>
        <div
          v-if="waiting"
          class="fdai-msg fdai-msg--bot"
        >
          <div class="fdai-dots">
            <span /><span /><span />
          </div>
        </div>
      </div>

      <form
        class="fdai-input-wrap"
        @submit.prevent="send"
      >
        <input
          v-model="inputText"
          class="fdai-input"
          placeholder="Что изменить..."
          :disabled="waiting"
        >
        <button
          type="submit"
          class="fdai-send"
          :disabled="!inputText.trim() || waiting"
        >
          <feather-icon
            icon="SendIcon"
            size="14"
          />
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import { getConfig } from '@/cubus/services/cubusService'
import { getSessionToken } from '@/cubus/jwt/tokenHolder'

// Import getConfig properly
function getBaseURL() {
  try {
    const cfg = require('@/libs/config.json')
    return cfg.baseURL || 'http://localhost:9000'
  } catch (e) {
    return 'http://localhost:9000'
  }
}

export default {
  name: 'CbsFormDesignerAi',
  props: {
    formParam: { type: Object, default: null },
    objectId: { type: Number, default: 0 },
    entityMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isOpen: false,
      inputText: '',
      messages: [],
      waiting: false,
      ws: null,
      wsStatus: 'offline',
    }
  },
  beforeDestroy() {
    this.closeWs()
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen
      if (this.isOpen && !this.ws) this.connectWs()
    },

    connectWs() {
      this.closeWs()
      this.wsStatus = 'connecting'
      const base = getBaseURL()
      const url = new URL('/ws/chat', base)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'

      this.ws = new WebSocket(url.toString())
      this.ws.onopen = () => {
        this.wsStatus = 'online'
        this.ws.send(JSON.stringify({ type: 'auth', token: getSessionToken() || '' }))
      }
      this.ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'message') {
            this.onBotMessage(data.text || '', data)
          }
        } catch (e) { /* ignore */ }
      }
      this.ws.onclose = () => { this.wsStatus = 'offline' }
      this.ws.onerror = () => { this.wsStatus = 'offline' }
    },

    closeWs() {
      if (this.ws) { try { this.ws.close() } catch (e) { /* */ } this.ws = null }
    },

    sendText(text) {
      this.inputText = text
      this.send()
    },

    send() {
      const text = this.inputText.trim()
      if (!text || !this.ws || this.ws.readyState !== WebSocket.OPEN) return

      this.messages.push({ role: 'user', text })
      this.inputText = ''
      this.waiting = true
      this.scrollBottom()

      // Build context message with form_param
      const entityNames = Object.entries(this.entityMap).slice(0, 80).map(([id, i]) => `${id}:${i.name}${i.datatype ? `(${i.datatype})` : ''}`).join(', ')
      const instructions = `
ТЫ — ПОМОЩНИК КОНСТРУКТОРА ФОРМ PivotInput. Знай правила структуры:

СТРУКТУРА form_param:
- rows[] — уровни иерархии, порядок = порядок вложенности (row[0] верхний → row[last] детальный)
- rows[i] имеет: objectid, mode, columns, results, filter, groupby, addrecord
- mode: "record" = детальные записи (всегда последний уровень); "tree"/"tree_record" = дерево; отсутствует = группировка
- columns[]: { entityid, type, editable, align, destination_entityid, scalarid, indent }
- Для aggr_sum: { type:"aggr_sum", align:"right", destination_entityid: <entityid суммы>, entityid: null }
- Для пустой колонки: { entityid: null }
- results[]: { source_entityid: X, destination_entityid: Y } — передаёт X от родителя как Y в дочерний фильтр

ГРУППИРОВКА — ВСЕГДА ВЫБИРАЕТСЯ ПО ПОЛЮ (entity) дочернего уровня. Тип определяется АВТОМАТИЧЕСКИ по типу entity:

1) Если entity = REF (есть refobjectid у entity, т.е. ссылка на справочник):
   - objectid уровня = refobjectid этого entity (целевой справочник)
   - results = [{source_entityid: 1, destination_entityid: <entityid>}]
   - groupby отсутствует
   - Пример: поле "Статья БДР" (entity 244, refobjectid=95) → objectid=95, results=[{1, 244}]

2) Если entity = текстовое/скалярное (нет refobjectid):
   - objectid уровня = objectid дочернего (тот же)
   - groupby: { isactive:true, groups:[{isactive:true, oper:{sid:"group"}, entity:{value: <entityid>}}] }
   - results = [{source_entityid: <entityid>, destination_entityid: <entityid>}] (self-ref)
   - Пример: поле "Назначение" (entity 4076, string) → objectid=<child>, groupby={entity:4076}, results=[{4076, 4076}]

ВЫБОР ПОЛЯ:
- Пользователь НАЗЫВАЕТ поле ("по полю Х", "по Х", "группировка по Х")
- Найди entity по имени в словаре ниже
- Смотришь тип: если это ref (есть refobjectid у entity) — делаешь REF; иначе — TEXT
- Пользователь может не знать, справочник это или текст — определяй САМ по типу entity

ЗАПРЕЩЕНО:
- Предлагать группировку по полю которого нет в записях дочернего уровня
- Смешивать типы (не делай objectid справочника И groupby одновременно)

АВТОЗАПОЛНЕНИЕ КОЛОНОК уровня группы:
- Пройди по columns дочернего (record) уровня
- Для числовых полей (money/decimal/double/int) → { type:"aggr_sum", align:"right", destination_entityid: ENT, entityid: null } на ТОЙ ЖЕ позиции
- Для первой "name"-колонки в группе: если REF — используй destination_entityid ref-поля; если TEXT — entityid = entity группы (показывает значение)
- Остальные нечисловые позиции → { entityid: null } (spacer)
- Позиции должны совпадать с record-уровнем чтобы суммы визуально оказались под своими значениями

ФИЛЬТРЫ paramentity: если родитель передаёт через results source→dest, то дочерний должен иметь в filter условие { entity: dest, oper:"equal", arg: { type:"paramentity", value: dest } }.

ВСТАВКА НОВОГО УРОВНЯ (critical):
Когда пользователь просит "добавить уровень группировки", НЕ МЕНЯЙ существующие уровни без необходимости. Правила:

1) results КАЖДОГО уровня — это ТОЛЬКО то что ЭТОТ уровень передаёт ВНИЗ в дочерний (одна связка). НЕ копируй results родителя в дочерний уровень.
2) Если родитель уже передавал значение дочернему (например row[0].results=[{source:1,dest:244}] → row[1] фильтрует по 244 через paramentity), и ты вставляешь новый промежуточный уровень — этот новый уровень должен:
   a) Иметь в своём filter paramentity 244 (принимает от деда-родителя)
   b) Передать 244 дальше вниз ТОЛЬКО если детальному record нужно фильтроваться по нему (обычно уже отражено в его filter)
   c) Свои results — ТОЛЬКО self-ref для СВОЕГО ключа группировки [{source:groupEntity, destination:groupEntity}]
   d) НЕ дублируй парентовы results
3) Для TEXT-группы results = ровно [{source_entityid:<groupEntity>, destination_entityid:<groupEntity>}]. Ничего больше.
4) Для REF-группы results = ровно [{source_entityid:1, destination_entityid:<ref_entity_дочернего>}] (source=1 это id записи справочника).
5) paramentity фильтры deep-child наследует от ВСЕХ предков: если row[0] передаёт 244 и новый row[1] передаёт 4076, то record row[2] должен иметь paramentity и на 244 и на 4076.
6) Никогда не меняй существующий фильтр без явного запроса — только ДОБАВЛЯЙ недостающее.`

      const contextMsg = `${instructions}\n\n[ТЕКУЩИЙ form_param]\n${JSON.stringify(this.formParam, null, 2)}\n\n[СЛОВАРЬ ENTITY id:имя(тип)]: ${entityNames}\n\n[ЗАПРОС]: ${text}\n\nВерни ПОЛНЫЙ изменённый form_param JSON в блоке \`\`\`json ... \`\`\` (целиком, не фрагмент). Кратко объясни что изменил и ПОЧЕМУ выбрал REF или TEXT группировку.`

      this.ws.send(JSON.stringify({
        type: 'message',
        to: 'chatbot',
        text: contextMsg,
        useAi: true,
        token: getSessionToken() || '',
      }))
    },

    onBotMessage(text, data) {
      this.waiting = false
      // Try to extract JSON from response
      const jsonMatch = text.match(/```json\s*([\s\S]*?)```/)
      let formParam = null
      if (jsonMatch) {
        try {
          formParam = JSON.parse(jsonMatch[1].trim())
        } catch (e) { /* not valid json */ }
      }
      // Also try raw JSON block
      if (!formParam) {
        const rawMatch = text.match(/\{[\s\S]*"rows"[\s\S]*\}/)
        if (rawMatch) {
          try { formParam = JSON.parse(rawMatch[0]) } catch (e) { /* */ }
        }
      }

      const cleanText = text.replace(/```json[\s\S]*?```/g, '').replace(/\{[\s\S]*"rows"[\s\S]*\}/g, '').trim()

      this.messages.push({
        role: 'bot',
        text: cleanText || (formParam ? 'Изменения готовы:' : text),
        formParam,
        applied: false,
        discarded: false,
      })
      this.scrollBottom()
    },

    applyMsg(mi) {
      const msg = this.messages[mi]
      if (!msg || !msg.formParam) return
      msg.applied = true
      this.$emit('apply', msg.formParam)
    },

    discardMsg(mi) {
      const msg = this.messages[mi]
      if (msg) msg.discarded = true
    },

    formatText(text) {
      if (!text) return ''
      // BUG-820: текст — ответ ИИ, а результат идёт в v-html.
      return DOMPurify.sanitize(text.replace(/\n/g, '<br>').replace(/`([^`]+)`/g, '<code>$1</code>'))
    },

    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messages
        if (el) el.scrollTop = el.scrollHeight
      })
    },
  },
}
</script>

<style scoped>
.fdai { margin-top: 12px; }
.fdai-toggle { display: flex; align-items: center; gap: 6px; padding: 8px 14px; background: #f0eeff; border: 1px solid #d4cfff; border-radius: 6px; cursor: pointer; font-size: 12px; color: #7367f0; font-weight: 500; }
.fdai-toggle:hover { background: #e8e5ff; }
.fdai--open .fdai-toggle { border-radius: 6px 6px 0 0; border-bottom: none; background: #7367f0; color: #fff; }
.fdai-status { width: 6px; height: 6px; border-radius: 50%; }
.fdai-status--on { background: #66bb6a; }
.fdai-status--off { background: #ccc; }
.fdai-panel { border: 1px solid #d4cfff; border-top: none; border-radius: 0 0 6px 6px; background: #fff; }
.fdai-messages { max-height: 350px; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.fdai-msg { padding: 8px 12px; border-radius: 8px; font-size: 12px; line-height: 1.5; max-width: 92%; }
.fdai-msg--user { background: #7367f0; color: #fff; align-self: flex-end; border-bottom-right-radius: 2px; }
.fdai-msg--bot { background: #f5f5f5; color: #333; align-self: flex-start; border-bottom-left-radius: 2px; }
.fdai-msg-text code { background: #e8e5ff; padding: 1px 4px; border-radius: 2px; font-size: 11px; }
.fdai-examples { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.fdai-examples span { color: #7367f0; cursor: pointer; font-size: 11px; }
.fdai-examples span:hover { text-decoration: underline; }
.fdai-msg-actions { display: flex; gap: 6px; margin-top: 8px; }
.fdai-btn { padding: 4px 12px; font-size: 11px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; background: #f5f5f5; color: #666; display: flex; align-items: center; gap: 4px; }
.fdai-btn--apply { background: #7367f0; color: #fff; border-color: #7367f0; }
.fdai-msg-status { font-size: 10px; margin-top: 4px; }
.fdai-msg-status--ok { color: #2e7d32; }
.fdai-msg-status--no { color: #999; }
.fdai-dots { display: flex; gap: 4px; }
.fdai-dots span { width: 6px; height: 6px; background: #bbb; border-radius: 50%; animation: fdai-b 1.2s infinite; }
.fdai-dots span:nth-child(2) { animation-delay: .2s; }
.fdai-dots span:nth-child(3) { animation-delay: .4s; }
@keyframes fdai-b { 0%,80%,100% { transform: scale(.8); opacity: .5; } 40% { transform: scale(1.2); opacity: 1; } }
.fdai-input-wrap { display: flex; border-top: 1px solid #eee; }
.fdai-input { flex: 1; padding: 10px 12px; font-size: 13px; border: none; outline: none; }
.fdai-send { padding: 8px 12px; background: none; border: none; color: #7367f0; cursor: pointer; }
.fdai-send:disabled { color: #ddd; }
</style>
